import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';

/**
 * Authentication service base URL from environment
 */
const AUTH_BASE_URL = (window as any).__ENV_CONFIG__?.AUTH_URL || process.env.REACT_APP_AUTHENTICATION_URL || '';

/**
 * User session data from auth-service
 */
export interface UserSession {
    authenticated: boolean;
    loginRequired?: boolean;
    loginUrl?: string;
    userId?: number;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    authorities?: string[];
    ecmUser?: boolean;
    spectreUser?: boolean;
    sessionCreatedAt?: string;
}

/**
 * Auth context value type
 */
interface AuthContextValue {
    session: UserSession | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    sessionExpired: boolean;
    loginFailed: boolean;
    logout: () => Promise<void>;
    checkSession: () => Promise<void>;
    handleSessionExpiredLogin: () => void;
    handleLoginFailureRetry: () => void;
    clearLoginFailure: () => void;
}

/**
 * Default context value
 */
const defaultContextValue: AuthContextValue = {
    session: null,
    isLoading: true,
    isAuthenticated: false,
    sessionExpired: false,
    loginFailed: false,
    logout: async () => { },
    checkSession: async () => { },
    handleSessionExpiredLogin: () => { },
    handleLoginFailureRetry: () => { },
    clearLoginFailure: () => { }
};

/**
 * Auth context for sharing authentication state across components
 */
export const AuthContext = createContext<AuthContextValue>(defaultContextValue);

/**
 * Hook to access auth context
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Auth provider props
 */
interface AuthProviderProps {
    children: ReactNode;
}

/**
 * Auth provider component that manages authentication state.
 * After initial login, monitors session health via:
 * - Visibility change (tab re-focus)
 * - Periodic heartbeat (every 5 minutes)
 * Shows a session-expired dialog when 401 is received after user was authenticated.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [session, setSession] = useState<UserSession | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);
    // Track if redirect is in progress to prevent loops
    const isRedirecting = useRef(false);
    // Track if session has been checked to prevent duplicate calls (e.g., React StrictMode)
    const hasCheckedSession = useRef(false);
    // Track if the user was previously authenticated (to distinguish initial login from expiry)
    const wasAuthenticated = useRef(false);

    /**
     * Check session with auth-service.
     * @param isBackgroundCheck - if true, this is a periodic/visibility check (not initial load)
     */
    const checkSession = useCallback(async (isBackgroundCheck: boolean = false) => {
        try {
            if (!isBackgroundCheck) {
                setIsLoading(true);
            }
            const response = await fetch(`${AUTH_BASE_URL}/session`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (response.status === 401) {
                if (wasAuthenticated.current) {
                    // User WAS authenticated but session expired → show dialog
                    setSession({ authenticated: false });
                    (window as any).__USER_SESSION__ = null;
                    setSessionExpired(true);
                    return;
                }
                // Not authenticated on initial load — redirect to login immediately
                if (!isRedirecting.current) {
                    // Detect redirect loops: if we redirected very recently, don't redirect again
                    const lastRedirect = sessionStorage.getItem('auth_redirect_ts');
                    const now = Date.now();
                    if (lastRedirect && now - parseInt(lastRedirect, 10) < 5000) {
                        console.error('Auth redirect loop detected — not redirecting again');
                        setSession({ authenticated: false });
                        return;
                    }
                    isRedirecting.current = true;
                    sessionStorage.setItem('auth_redirect_ts', now.toString());
                    // Try to extract loginUrl from response body, fall back to /login
                    let loginPath = '/login';
                    try {
                        const data: UserSession = await response.json();
                        if (data.loginUrl) {
                            loginPath = data.loginUrl.replace('/api/auth', '');
                        }
                    } catch (e) {
                        // Response may not be valid JSON — use default login path
                    }
                    const currentPath = window.location.pathname + window.location.search;
                    const loginUrl = `${AUTH_BASE_URL}${loginPath}?redirectUrl=${encodeURIComponent(currentPath)}`;
                    window.location.href = loginUrl;
                    return;
                }
                setSession({ authenticated: false });
            } else if (response.ok) {
                const data: UserSession = await response.json();
                // Clear any redirect-loop marker on successful auth
                sessionStorage.removeItem('auth_redirect_ts');
                // Store session data on window for single-spa route guards
                (window as any).__USER_SESSION__ = data;
                setSession(data);
                wasAuthenticated.current = true;
                setSessionExpired(false);
                // Trigger single-spa to re-evaluate active applications.
                // This is needed when the page loads directly on a microfrontend route
                // (e.g. full page reload or new tab), because single-spa's start() runs
                // synchronously before the async session fetch completes, so activeWhen
                // initially returns false. Dispatching popstate forces a re-check.
                if (!isBackgroundCheck) {
                    window.dispatchEvent(new PopStateEvent('popstate'));
                }
            }
        } catch (error) {
            console.error('Session check failed:', error);
            if (wasAuthenticated.current) {
                // Network error after being authenticated — treat as potential expiry
                setSession({ authenticated: false });
                (window as any).__USER_SESSION__ = null;
                setSessionExpired(true);
            } else {
                setSession({ authenticated: false });
            }
        } finally {
            // Keep loading state while redirecting to SSO to prevent landing page flash
            if (!isRedirecting.current) {
                setIsLoading(false);
            }
        }
    }, []);

    /**
     * Handle login redirect from the session-expired dialog
     */
    const handleSessionExpiredLogin = useCallback(() => {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = `${AUTH_BASE_URL}/login?redirectUrl=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
    }, []);

    /**
     * Handle retry from login failure dialog
     */
    const handleLoginFailureRetry = useCallback(() => {
        setLoginFailed(false);
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = `${AUTH_BASE_URL}/login?redirectUrl=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
    }, []);

    /**
     * Clear login failure state
     */
    const clearLoginFailure = useCallback(() => {
        setLoginFailed(false);
    }, []);

    /**
     * Logout - calls auth-service logout and clears session
     */
    const logout = async () => {
        let sloUrl: string | null = null;
        try {
            const response = await fetch(`${AUTH_BASE_URL}/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                sloUrl = data.sloUrl || null;
            }
        } catch (error) {
            console.error('Logout failed:', error);
        }

        // Clear local state regardless of backend response
        setSession({ authenticated: false });
        (window as any).__USER_SESSION__ = null;
        wasAuthenticated.current = false;

        if (sloUrl) {
            // Redirect to IdP Single Logout to terminate the SSO session
            // (same pattern as ecm-service's OidcLogoutSuccessHandler)
            window.location.href = sloUrl;
        } else {
            // No SLO URL configured — show session-expired dialog
            setSessionExpired(true);
        }
    };

    // Check for login error in URL on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const loginError = urlParams.get('loginError');
        if (loginError === 'true') {
            setLoginFailed(true);
            // Clean up URL without reloading
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // Check session on mount
    useEffect(() => {
        // Prevent duplicate calls (e.g., React StrictMode double-mounting)
        if (hasCheckedSession.current) {
            return;
        }
        hasCheckedSession.current = true;
        checkSession(false);
    }, [checkSession]);

    // Visibility change: re-check session when user returns to the tab
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && wasAuthenticated.current && !sessionExpired) {
                checkSession(true);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [checkSession, sessionExpired]);

    // Periodic heartbeat: check session every 20 minutes while authenticated
    useEffect(() => {
        if (!wasAuthenticated.current || sessionExpired) {
            return;
        }

        const intervalId = setInterval(() => {
            if (wasAuthenticated.current && !sessionExpired) {
                checkSession(true);
            }
        }, 20 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, [checkSession, sessionExpired, session]);

    const value: AuthContextValue = {
        session,
        isLoading,
        isAuthenticated: session?.authenticated ?? false,
        sessionExpired,
        loginFailed,
        logout,
        checkSession: () => checkSession(false),
        handleSessionExpiredLogin,
        handleLoginFailureRetry,
        clearLoginFailure
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
